import { getTrackingIds } from 'Lib/trackingConfig'

const DISABLE_ON_DEV = true

// Migrated from our self-hosted AWS Signals Gateway instance to Stape-hosted
// Signals Gateway (Apr 2026) due to recurring 1–2 day outages of the AWS
// instance. Both the SDK script and the event endpoint are routed through
// our own bodyfast.app subdomain (CNAME → sgw.stape.ltd) so that adblockers
// that target stape.ltd domains don't break tracking. The original AWS-routed
// values are kept commented below so we can flip back quickly if Stape has
// issues.
//
// AWS (legacy — keep for rollback):
// const SIGNALS_GATEWAY_SCRIPT_SRC = 'https://d1grjgeaqdtl7n.cloudfront.net/sdk/7743123154992410133/events.js'
// const SIGNALS_GATEWAY_ID = '7743123154992410133'
// const SIGNALS_GATEWAY_HOST = 'https://routing2.bodyfast.app/'

// Per-domain gateway ids (see Lib/trackingConfig.ts).
const SIGNALS_GATEWAY_IDS = getTrackingIds().signalsGatewayIds
const SIGNALS_GATEWAY_SCRIPT_ID = 'signals_gateway_script'
const SIGNALS_GATEWAY_HOST = 'https://routingstape.bodyfast.app/'

// events.js bundles the SDK core plus the config bootstrap (plugin opt-ins,
// AutomaticMatching keys, configLoaded) for the single ID baked into its URL.
// events-config.js is just that config bootstrap on its own.
const getSignalsGatewaySdkScriptSrc = (id: string) =>
  `${SIGNALS_GATEWAY_HOST}sdk/${id}/events.js`

const getSignalsGatewayConfigScriptSrc = (id: string) =>
  `${SIGNALS_GATEWAY_HOST}sdk/${id}/events-config.js`

const appendScript = (src: string, id?: string): HTMLScriptElement => {
  const script = document.createElement('script')
  script.async = true
  script.src = src
  if (id) script.id = id

  document.head.appendChild(script)
  return script
}

// We noticed that the cookie consent state is stacking up on Signals Gateway Pixel.
// This means that if we revoke consent multiple times, we have to grant it the same number of times
// to get it working again. To avoid this issue, we track the current consent state ourselves.
let cookieConsentGranted: boolean | null = null

// The SDK processes direct consent calls even while its event queue is frozen,
// but a consent call sitting IN the queue cannot lift a freeze created by an
// earlier queued revoke. Returning visitors with saved cookie consent hit
// exactly that: OneTrust grants ~100ms after app start, before events.js has
// loaded, so the grant is queued behind the initial revoke's freeze and the
// whole session deadlocks — every event is silently dropped. Replaying the
// granted state with a direct call on the live SDK lifts the freeze. A second
// 'grant' is harmless: the SDK's unfreeze is a no-op when nothing is frozen.
const replaySignalsGatewayConsentState = () => {
  if (cookieConsentGranted === true) {
    window.cbq?.('consent', 'grant')
  }
}

/**
 * This is a more readable and improved version of the Stape-provided snippet:
 *
 *  <script>
 *    !function(a,h,e,v,n,t,s){
 *      if(a.cbq)return;
 *      n=a.cbq=function(){n.callMethod ? n.callMethod.apply(n,arguments) : n.queue.push(arguments);};
 *      if(!a._cbq)a._cbq=n;
 *      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];
 *      t=h.createElement(e);t.async=!0; t.src=v;s=h.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s);
 *    }(window, document,'script', 'https://routingstape.bodyfast.app/sdk/2402056774949389045/events.js');
 *    cbq('setHost', 'https://routingstape.bodyfast.app/');
 *    cbq('init', '2402056774949389045');
 *    cbq('track', 'PageView');   // not mirrored — fired post-consent by
 *                                // grantFacebookConsent() (Facebook.service.ts)
 *  </script>
 */
export const initSignalsGatewayPixel = () => {
  if (__DEV__ && DISABLE_ON_DEV) return
  if (typeof window === 'undefined') return

  if (window.cbq) return

  // No ids for this domain (e.g. my2 until it gets its own): nothing to init, and
  // loading sdk/undefined/events.js would 404.
  if (SIGNALS_GATEWAY_IDS.length === 0) return

  const cbq: SignalsGatewayFn = (window.cbq = function (...args: any[]) {
    if (cbq.callMethod) {
      cbq.callMethod.apply(cbq, args)
    } else {
      cbq.queue.push(args)
    }
  } as SignalsGatewayFn)

  if (!window._cbq) window._cbq = cbq

  cbq.push = cbq
  cbq.loaded = true
  cbq.version = '2.0'
  cbq.queue = []

  // Load the SDK script ONCE, then init each gateway ID against it (mirrors the
  // Meta pixel multi-pixel pattern in Facebook.service.ts). Loading a separate
  // events.js per ID makes both scripts fight over the shared window.cbq global:
  // the script that loads second rebinds cbq.callMethod to an instance whose
  // init was already drained from the queue by the first script, so all
  // subsequent track events are silently dropped.
  //
  // events.js only carries the config bootstrap for the first ID, and the SDK
  // does not fetch configs for IDs init'ed through the pre-load queue. Without
  // their config, additional pixels still send events but miss their
  // FirstPartyCookies / AutomaticMatching opt-ins, so we load events-config.js
  // for every additional ID ourselves. The config scripts call methods that
  // only exist on the real SDK cbq object, so they must load AFTER events.js —
  // hence the onload chaining.
  if (!document.getElementById(SIGNALS_GATEWAY_SCRIPT_ID)) {
    const [primaryId, ...additionalIds] = SIGNALS_GATEWAY_IDS

    const sdkScript = appendScript(getSignalsGatewaySdkScriptSrc(primaryId), SIGNALS_GATEWAY_SCRIPT_ID)
    sdkScript.onload = () => {
      const configScripts = additionalIds.map(id => appendScript(getSignalsGatewayConfigScriptSrc(id)))

      // Replay only after every config script has settled so the additional
      // pixels are fully configured before the frozen queue drains — an event
      // drained before a pixel's config bootstrap ran is dropped for that
      // pixel (same race as the deferred PageView in grantSignalsGatewayConsent).
      let pendingConfigScripts = configScripts.length
      if (pendingConfigScripts === 0) {
        replaySignalsGatewayConsentState()
        return
      }

      configScripts.forEach(script => {
        const onSettled = () => {
          pendingConfigScripts -= 1
          if (pendingConfigScripts === 0) replaySignalsGatewayConsentState()
        }
        script.onload = onSettled
        script.onerror = onSettled
      })
    }
  }

  cbq('setHost', SIGNALS_GATEWAY_HOST)

  revokeSignalsGatewayConsent() // According to the docs, we need to revoke the consent before initializing: https://developers.facebook.com/docs/meta-pixel/implementation/gdpr/

  SIGNALS_GATEWAY_IDS.forEach(id => cbq('init', id))
}

export const revokeSignalsGatewayConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) return

  // We noticed that the cookie consent state is stacking up on Signals Gateway Pixel.
  // This means that if we revoke consent multiple times, we have to grant it the same number of times
  // to get it working again. To avoid this issue, we track the current consent state ourselves.
  if (cookieConsentGranted === false) {
    return
  }

  cookieConsentGranted = false
  window.cbq?.('consent', 'revoke')
}

export const grantSignalsGatewayConsent = () => {
  if (__DEV__ && DISABLE_ON_DEV) return

  // We noticed that the cookie consent state is stacking up on Signals Gateway Pixel.
  // This means that if we revoke consent multiple times, we have to grant it the same number of times
  // to get it working again. To avoid this issue, we track the current consent state ourselves.
  if (cookieConsentGranted === true) {
    return
  }

  cookieConsentGranted = true
  window.cbq?.('consent', 'grant')
}

export const trackSignalsGatewayEvent = (
  event: string,
  data: Record<string, any> = {},
  extraData: { eventID?: string } = {},
) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log(`Tracking Signals Gateway event: ${event}`, data)
    return
  }

  if (!cookieConsentGranted && event === 'PageView') {
    // Signals Gateway is queueing all the events until consent is granted.
    // This is causing multiple PageView events from the same page to be sent after consent is granted.
    // To avoid this, we skip sending PageView events when consent is not granted.
    // Error message on the extension: The Signals Gateway Pixel activated X times on this web page, which can cause errors in your event tracking.
    return
  }

  window.cbq?.('track', event, data, extraData)
}

export const trackCustomSignalsGatewayEvent = (
  event: string,
  data: Record<string, any> = {},
  extraData: { eventID?: string } = {},
) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    console.log(`Tracking Signals Gateway custom event: ${event}`, data)
    return
  }

  window.cbq?.('trackCustom', event, data, extraData)
}
